import React, { useContext } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { formatPhoneNumber } from "../../utils/EmpUtil";
import { useTranslation } from "react-i18next";
import EMPObjectCard from "../../shared/components/EMPDataCard/EMPObjectCard";
import { UserContext } from "../../shared/context/userContext";

const LincolnInfoCard = ({ data, columns, testId }) => (
  <Box
    component="dl"
    data-testid={testId}
    sx={{
      m: 0,
      p: 4,
      width: "100%",
      minHeight: 128,
      boxSizing: "border-box",
      border: "1px solid #B2B2B2",
      borderRadius: "8px",
      backgroundColor: "#FFFFFF",
      display: "grid",
      gridTemplateColumns: {
        xs: "minmax(0, 1fr)",
        sm: "repeat(2, minmax(0, 1fr))",
        md: `repeat(${columns}, minmax(0, 1fr))`,
      },
      gap: 4,
      alignItems: "start",
    }}
  >
    {Object.entries(data).map(([key, item]) => (
      <Box key={key} sx={{ minWidth: 0 }}>
        <Typography component="dt" variant="caption" sx={{ mb: 1 }}>
          {item.heading}
        </Typography>
        <Typography
          component="dd"
          variant="body2"
          sx={{ m: 0, whiteSpace: "pre-line", overflowWrap: "anywhere" }}
        >
          {item.content}
        </Typography>
      </Box>
    ))}
  </Box>
);

LincolnInfoCard.propTypes = {
  data: PropTypes.objectOf(
    PropTypes.shape({
      heading: PropTypes.node.isRequired,
      content: PropTypes.node.isRequired,
    })
  ).isRequired,
  columns: PropTypes.number.isRequired,
  testId: PropTypes.string.isRequired,
};

const extractCustomerDetails = (content, t) => {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const registrationMonth = content?.customerReservation?.registrationMonth
    ? monthNames[content.customerReservation.registrationMonth - 1]
    : "";

  let licensePlateDecision;
  const keepLicensePlateFlag =
    content?.customerReservation?.keepLicensePlateFlag;

  if (keepLicensePlateFlag === true) {
    licensePlateDecision = t(
      "customerInfo.licenseAndRegistration.licensePlateDecisionStatus.0"
    );
  } else if (keepLicensePlateFlag === false) {
    licensePlateDecision = t(
      "customerInfo.licenseAndRegistration.licensePlateDecisionStatus.1"
    );
  }

  return {
    name: `${content?.customerInfo?.firstName || "--"} ${
      content?.customerInfo?.lastName || "--"
    }`.trim(),
    address: `${content?.customerInfo?.address1 || "--"} ${
      content?.customerInfo?.city || "--"
    }, ${content?.customerInfo?.state || "--"} ${
      content?.customerInfo?.zip || "--"
    }`.trim(),
    phone:
      content?.customerInfo?.phoneNumber ||
      content?.customerInfo?.mobilePhoneNumber ||
      "",
    email: content?.customerInfo?.email || "--",
    registrationMonth: registrationMonth || "--",
    licensePlateDecision: licensePlateDecision || "--",
  };
};

const CustomerInfo = ({ content, isPreOrder }) => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext) || {};
  const isLincolnBrand = user?.brand === "L";
  const customerDetails = extractCustomerDetails(content, t);

  const customer = content?.customerInfo || {};
  const streetAddress = [
    customer.address1,
    customer.address2,
    customer.address3,
    customer.address4,
  ]
    .filter(Boolean)
    .join("\n");
  const cityStateZip = [
    customer.city,
    [customer.state, customer.zip].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  const phoneNumbers = [customer.phoneNumber, customer.mobilePhoneNumber]
    .filter(Boolean)
    .filter(
      (phone, index, phones) =>
        phones.findIndex(
          (other) => other.replace(/\D/g, "") === phone.replace(/\D/g, "")
        ) === index
    );

  const contactDetails = {
    name: {
      heading: t("customerInfo.contactDetails.fields.0"),
      content: isLincolnBrand
        ? [customer.firstName, customer.lastName].filter(Boolean).join(" ") ||
          "--"
        : customerDetails.name,
    },
    address: {
      heading: t("customerInfo.contactDetails.fields.1"),
      content: isLincolnBrand
        ? [streetAddress, cityStateZip].filter(Boolean).join("\n") || "--"
        : customerDetails.address,
    },
    phone: {
      heading: t("customerInfo.contactDetails.fields.2"),
      content: isLincolnBrand ? (
        phoneNumbers.length ? (
          <Box component="span" sx={{ display: "grid", gap: 1 }}>
            {phoneNumbers.map((phone) => (
              <Box component="span" key={phone} sx={{ display: "block" }}>
                {formatPhoneNumber(phone)}
                {phone === customer.phoneNumber && (
                  <Box component="span" sx={{ display: "block", mt: 1 }}>
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        display: "inline-block",
                        backgroundColor: "#E8F5E9",
                        color: "#324047",
                        borderRadius: "2px",
                        px: 1,
                      }}
                    >
                      {t("customerInfo.contactDetails.preferred")}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        ) : (
          "--"
        )
      ) : customerDetails.phone ? (
        formatPhoneNumber(customerDetails.phone)
      ) : (
        "--"
      ),
    },
    email: {
      heading: t("customerInfo.contactDetails.fields.3"),
      content: customerDetails.email,
    },
  };

  const licenseAndRegistration = {
    registrationMonth: {
      heading: t("customerInfo.licenseAndRegistration.fields.0"),
      content: customerDetails.registrationMonth,
    },
    licensePlateDecision: {
      heading: t("customerInfo.licenseAndRegistration.fields.1"),
      content: customerDetails.licensePlateDecision,
    },
  };

  if (isLincolnBrand) {
    const headingStyles = {
      color: "text.primary",
      fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
      fontSize: "24px",
      fontWeight: 600,
      lineHeight: "32px",
      mb: 6,
    };

    return (
      <Box sx={{ display: "grid", gap: 14 }}>
        <Box>
          <Typography component="h2" variant="h3" sx={headingStyles}>
            {t("customerInfo.contactDetails.heading")}
          </Typography>
          <LincolnInfoCard
            data={contactDetails}
            columns={5}
            testId="lincoln-contact-details"
          />
        </Box>
        <Box>
          <Typography component="h2" variant="h3" sx={headingStyles}>
            {t("customerInfo.licenseAndRegistration.heading")}
          </Typography>
          <LincolnInfoCard
            data={licenseAndRegistration}
            columns={2}
            testId="lincoln-license-registration"
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(12, 1fr)"
      columnGap={4}
      rowGap={4}
    >
      <Box
        gridColumn={{
          xs: "span 12",
        }}
      >
        <Typography variant="h3" sx={{ color: "text.primary" }}>
          {t("customerInfo.contactDetails.heading")}
        </Typography>
      </Box>
      <Box
        gridColumn={{
          xs: "span 12",
        }}
      >
        <EMPObjectCard data={contactDetails} />
      </Box>

      {!isPreOrder && (
        <>
          <Box
            gridColumn={{
              xs: "span 12",
            }}
            sx={{ mt: 2 }}
          >
            <Typography variant="h3" sx={{ color: "text.primary" }}>
              {t("customerInfo.licenseAndRegistration.heading")}
            </Typography>
          </Box>
          <Box
            gridColumn={{
              xs: "span 12",
            }}
          >
            <EMPObjectCard data={licenseAndRegistration} />
          </Box>
        </>
      )}
    </Box>
  );
};

CustomerInfo.propTypes = {
  content: PropTypes.shape({
    customerInfo: PropTypes.shape({
      address1: PropTypes.string,
      address2: PropTypes.string,
      address3: PropTypes.string,
      address4: PropTypes.string,
      city: PropTypes.string,
      email: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      mobilePhoneNumber: PropTypes.string,
      phoneNumber: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
    }),
    customerReservation: PropTypes.shape({
      keepLicensePlateFlag: PropTypes.bool,
      registrationMonth: PropTypes.number,
    }),
  }).isRequired,
  isPreOrder: PropTypes.bool,
};

CustomerInfo.defaultProps = {
  isPreOrder: false,
};

export default CustomerInfo;
